import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Share2, FileDown, Users } from "lucide-react";
import { toast } from "sonner";
import { downloadElementAsPdf } from "@/lib/pdf";

// Bloc « Partager avec l'entourage » : lien public du même questionnaire en rôle « proche ».
export const ProcheShare = ({ moduleKey, role, patientName, title, text }) => {
  const url = `${window.location.origin}/p/${moduleKey}?role=proche${patientName ? `&pour=${encodeURIComponent(patientName)}` : ""}`;
  if (role === "proche") return null;
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Lien pour l'entourage copié"); } catch { toast.error("Copie impossible"); } };
  const share = async () => { if (!navigator.share) return copy(); try { await navigator.share({ title, text, url }); } catch { /* annulé */ } };
  return (
    <section className="rounded-3xl border border-[var(--border)] bg-white/70 p-5 sm:p-6" data-testid={`${moduleKey}-proche-share`}>
      <h3 className="font-serif-title text-2xl text-[var(--gold)] flex items-center gap-2"><Users size={20} /> Partager avec l'entourage</h3>
      <p className="text-sm text-[var(--ink-soft)] mt-1 mb-4">{text} Chaque personne remplit sa propre version sur son appareil puis exporte son PDF pour le praticien.</p>
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="rounded-2xl bg-white border border-[var(--border)] p-2 shrink-0"><QRCodeSVG value={url} size={112} fgColor="#4A3B36" /></div>
        <div className="flex-1 w-full space-y-2">
          <div className="rounded-full bg-white border border-[var(--border)] px-4 py-2 text-xs text-[var(--ink)] break-all select-all" data-testid={`${moduleKey}-proche-url`}>{url}</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copy} data-testid={`${moduleKey}-proche-copy`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--gold)] text-white text-sm font-semibold"><Copy size={14} /> Copier le lien</button>
            <button onClick={share} data-testid={`${moduleKey}-proche-send`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[var(--border)] text-[var(--ink)] text-sm font-semibold"><Share2 size={14} /> Envoyer…</button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Bouton d'export PDF + zone imprimable hors écran.
export const PdfExport = ({ filename, label = "Télécharger le PDF pour le praticien", testid, footer, children }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { await downloadElementAsPdf(ref.current, filename, { footer }); toast.success("PDF généré"); } catch { toast.error("Export PDF impossible"); } finally { setBusy(false); } };
  return (
    <>
      <button onClick={run} disabled={busy} data-testid={testid} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--ink)] text-white font-semibold shadow-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-60"><FileDown size={16} /> {busy ? "Préparation…" : label}</button>
      <div className="fixed -left-[9999px] top-0" aria-hidden="true"><div ref={ref} className="bg-white p-8 text-[#4A3B36]" style={{ width: 794, fontFamily: "Georgia, serif" }}>{children}</div></div>
    </>
  );
};

export const useLocalForm = (key, initial) => {
  const [data, setData] = useState(() => { try { return { ...initial, ...(JSON.parse(localStorage.getItem(key) || "null") || {}) }; } catch { return initial; } });
  const update = (fn) => setData((d) => { const n = typeof fn === "function" ? fn(d) : { ...d, ...fn }; try { localStorage.setItem(key, JSON.stringify(n)); } catch { /* quota */ } return n; });
  const reset = () => { localStorage.removeItem(key); setData(initial); };
  return [data, update, reset];
};

export const Progress = ({ value, testid }) => (
  <div className="h-2 rounded-full bg-[var(--beige)] overflow-hidden" data-testid={testid}><div className="h-full rounded-full bg-[var(--gold)] transition-all duration-500" style={{ width: `${Math.round(value * 100)}%` }} /></div>
);
